import React, {useState} from 'react';
import type {Data} from 'schemaDefinition/types';
import {Card, Spacer, Text} from 'components';
import {DataFormV1} from 'schema';
import {
  bookSchema,
  editConfiguration,
  errorBookSchema,
  sampleBook,
  warningBookSchema,
} from './demoSchema';

/**
 * Edit mode: `DataFormV1` renders an editable form from `bookSchema` and keeps
 * the edited entity in local state. The JSON of the current value is shown
 * below the form so the binding between schema and data is visible.
 *
 * Two further examples reuse the same `sampleBook` data with schema variants
 * whose validations the data deliberately fails, to show how the form surfaces
 * warnings and errors. They pass `showErrors` so every issue is highlighted
 * immediately, without having to visit each field first.
 */
export const EditPage: React.FC = () => {
  const [data, setData] = useState<Data>(sampleBook);
  const [warningData, setWarningData] = useState<Data>(sampleBook);
  const [errorData, setErrorData] = useState<Data>(sampleBook);

  return (
    <>
      <Text variant="body1" kind="secondary">
        {'A schema rendered as an editable form with <DataForm />. Every field, ' +
          'its type and validation comes from the schema definition.'}
      </Text>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'Edit book'}
          </Text>
        </Card.Header>
        <Card.Content>
          <DataFormV1
            id="schema-demo-edit"
            mode="edit"
            schema={bookSchema}
            data={data}
            relatedData={{}}
            configuration={editConfiguration}
            onDataChanged={setData}
          />
        </Card.Content>
      </Card>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'Current value'}
          </Text>
        </Card.Header>
        <Card.Content>
          <pre style={{margin: 0, whiteSpace: 'pre-wrap'}}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </Card.Content>
      </Card>
      <Spacer size={2} />
      <Text variant="body1" kind="secondary">
        {'The same data, but with a schema where every field has a validation ' +
          'that the value fails. Each part defines a `level: "warning"` ' +
          'validation, so every field is flagged as a warning.'}
      </Text>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'All fields warning'}
          </Text>
        </Card.Header>
        <Card.Content>
          <DataFormV1
            id="schema-demo-edit-warnings"
            mode="edit"
            schema={warningBookSchema}
            data={warningData}
            relatedData={{}}
            configuration={editConfiguration}
            showErrors={true}
            onDataChanged={setWarningData}
          />
        </Card.Content>
      </Card>
      <Spacer size={2} />
      <Text variant="body1" kind="secondary">
        {'The same data once more, but every validation uses the default ' +
          '`level: "error"`, so every field is flagged as a validation error.'}
      </Text>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'All fields error'}
          </Text>
        </Card.Header>
        <Card.Content>
          <DataFormV1
            id="schema-demo-edit-errors"
            mode="edit"
            schema={errorBookSchema}
            data={errorData}
            relatedData={{}}
            configuration={editConfiguration}
            showErrors={true}
            onDataChanged={setErrorData}
          />
        </Card.Content>
      </Card>
    </>
  );
};
